// ReservationModule.jsx
// ✅ Enterprise-grade Reservations Page (List + Calendar) — Production Ready
// - Uses Ant Design for layout + tables
// - Uses react-feather icons (no Ant icons for visuals)
// - Keeps ALL existing flows:
//   ✅ fetch tables + locations
//   ✅ fetch reservations by selected date
//   ✅ list view + calendar grid view (now uses ReservationCalendarGrid component)
//   ✅ filters (status/source/table/location) + search
//   ✅ exports (XLS/CSV/PDF/Print preview)
//   ✅ actions (edit/cancel/delete)
//   ✅ open modal for create/edit; click calendar cell to create; click booking opens drawer
//
// UX Improvements (enterprise):
// ✅ Unified header bar with hierarchy
// ✅ Date navigator + Today button
// ✅ Search in header
// ✅ View toggle pills
// ✅ Stats row with premium cards
// ✅ Better empty states
// ✅ Details Drawer for "click guest/customer" + "click calendar booking"
//
// Assumes you already created:
// - ./components/drawers/ReservationDetailsDrawer
// - ./components/calendar/ReservationCalendarGrid
// - ./components/popups/ReservationFormModal  (your multi-step modal)
// - exportService, PrintReportPreview exist

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Input, Table, Tag, Dropdown, Menu, Tooltip, Popover, Select, Row, Col } from "antd";
import moment from "moment";
import "moment-timezone";
import "./reservations.css";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

import {
  Filter as FilterIcon,
  List as ListIcon,
  Calendar as CalendarIcon,
  Download as DownloadIcon,
  Plus as PlusIcon,
  RefreshCw as RefreshIcon,
  ChevronLeft,
  ChevronRight,
  Settings as SettingsIcon,
  Search as SearchIcon,
  MoreVertical,
  XCircle,
  Trash2,
  Edit3,
} from "react-feather";

import { ORG_HOURS, STATUS_THEME } from "./mockData";
import ReservationFormModal from "./components/popups/ReservationFormModal";
import ReservationDetailsDrawer from "./components/reservations/ReservationDetailsDrawer";
import ReservationCalendarGrid from "./components/reservations/ReservationCalendarGrid";

import exportService from "../../pages/pos/utils/exportService";
import PrintReportPreview from "./reports/PrintReportPreview";
import { useNotification } from "../../context/NotificationContext";

const { Search } = Input;



// -----------------------------------------------------
// Helper: deterministic color from string (table color)
// -----------------------------------------------------
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < String(string || "").length; i++) {
    hash = String(string).charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }
  return color;
};

const niceTime = (iso) => (iso ? moment(iso).format("HH:mm") : "-");

export default function ReservationModule() {
  const { notify } = useNotification();

  // -------------------------
  // State
  // -------------------------
  const [tables, setTables] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedDate, setSelectedDate] = useState(moment().tz(ORG_HOURS.timezone));
  const [view, setView] = useState("list"); // "list" | "calendar"

  const [filters, setFilters] = useState({
    status: [],
    source: [],
    table: [],
    location: null,
    search: "",
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const [reservations, setReservations] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);

  // ✅ Drawer for details (click customer / click booking in calendar)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsReservation, setDetailsReservation] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const openDetails = (res) => {
    setDetailsReservation(res);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsReservation(null);
  };

const [waitlistIndex, setWaitlistIndex] = useState(new Map());
const apiUrl = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("token");

const normalizeStatusLabel = (s) => {
  const k = String(s || "").trim().toLowerCase();
  const map = {
    "cancelled": "Canceled",
    "canceled": "Canceled",
    "no-show": "No Show",
    "no_show": "No Show",
    "checkedin": "Checked In",
    "waitlisted": "Waitlisted",
    "confirmed": "Confirmed",
    "pending": "Pending",
    "seated": "Seated",
    "completed": "Completed",
  };
  return map[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : "-");
};


const fetchWaitlistIndex = useCallback(async () => {
  try {
    const res = await axios.get(`${apiUrl}/waitlist`, { headers: { Authorization: `Bearer ${token}` }});
    const raw = res.data?.data ?? res.data ?? [];
    const rows = Array.isArray(raw) ? raw : Object.values(raw || {});
    const map = new Map();

    rows.forEach((w) => {
      if (w.from_booking && w.booking_id && !["canceled","cancelled","seated"].includes(String(w.status || "").toLowerCase())) {
        map.set(String(w.booking_id), w);
      }
    });

    setWaitlistIndex(map);
  } catch {
    // ignore silently
  }
}, [apiUrl, token]);


  // -------------------------
  // Fetch: Tables
  // -------------------------
  const fetchTables = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/pos-module/tables`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let tablesList = Array.isArray(response.data) ? response.data : Object.values(response.data || {});
      tablesList = tablesList.map((table) => ({
        ...table,
        label: table.table_no,
        capacity: table.seating_capacity,
        color: stringToColor(table.id),
      }));

      setTables(tablesList);

      const uniqueLocations = [...new Set(tablesList.map((t) => t.location).filter(Boolean))];
      setLocations(uniqueLocations);
    } catch (error) {
      notify("error", error.response?.data?.error || "Failed to fetch tables.");
    }
  }, [notify]);

  // -------------------------
  // Fetch: Reservations by date
  // -------------------------
  const fetchReservations = useCallback(async () => {
    try {
      const date = selectedDate.format("YYYY-MM-DD");

      const response = await axios.get(`${apiUrl}/reservations/listings`, {
        params: { reservation_date: date },
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (response.data || []).map((row) => {
        const dateStr = moment(row.reservation_date).format("YYYY-MM-DD");
        const startTime = moment(row.reservation_time, "HH:mm:ss").format("HH:mm");
        const start = moment(`${dateStr} ${startTime}`).toISOString();
        const end = moment(start).add(row.expected_duration_minutes || 90, "minutes").toISOString();

        return {
          id: row.id,
          customerName: row.guest_name,
          phone: row.contact_number,
          email: row.email,
          partySize: row.party_size,
          start,
          end,
          tableId: row.assigned_table_id || "unassigned",
          source: row.reservation_source || "Phone",
          status: row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : "Confirmed",
          notes: row.special_requests || "",
          occasion: row.occasion || "",
        };
      });

      setReservations(data);
    } catch (error) {
      notify("error", error.response?.data?.error || "Failed to fetch reservations");
    }
  }, [notify, selectedDate]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  
  const addReservationToQueue = useCallback(
  async (resv) => {
    if (!resv?.id) return;

    // ✅ UI guard (prevents double click)
    const existing = waitlistIndex.get(String(resv.id));
    if (existing) {
      notify("info", "Already in queue");
      return;
    }

    setQueueLoading(true);
    try {
      const payload = {
        name: resv.customerName || resv.name || "Guest",
        phone: resv.phone || "",
        email: resv.email || null,
        party_size: Number(resv.partySize || 2),
        location: null,
        notes: `Arrived for ${moment(resv.start).format("h:mm A")} reservation (#${resv.id})`,
        source: "Booking",
        from_booking: true,
        booking_id: resv.id,
      };

      // 1) Create waitlist row (backend should return existing if already exists)
      const wlRes = await axios.post(`${apiUrl}/waitlist/add`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2) Update reservation status (match DB canonical)
      await axios.put(
        `${apiUrl}/reservations/edit/${resv.id}`,
        { status: "waitlisted", checked_in: true }, // ✅ recommended
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notify("success", "Added to queue (linked to reservation)");

      // 3) Refresh both lists
      await Promise.allSettled([fetchReservations(), fetchWaitlistIndex()]);

      // 4) Keep drawer updated instantly (consistent status)
      setDetailsReservation((prev) =>
        prev?.id === resv.id
          ? { ...prev, status: "Waitlisted" } // ✅ consistent with UI casing
          : prev
      );
    } catch (error) {
      const msg = error?.response?.data?.error || "Failed to add to queue";

      if (String(msg).toLowerCase().includes("already")) {
        notify("info", "This reservation is already in the queue");
        await fetchWaitlistIndex();
      } else {
        notify("error", msg);
      }
    } finally {
      setQueueLoading(false);
    }
  },
  [notify, fetchReservations, fetchWaitlistIndex, waitlistIndex]
);

  
  // -------------------------
  // Derived: filtered tables by location
  // -------------------------
  const filteredTables = useMemo(() => {
    if (!filters.location) return tables;
    return tables.filter((t) => t.location === filters.location);
  }, [tables, filters.location]);

  // -------------------------
  // Derived: filtered reservations by date & filters
  // -------------------------
  const filteredReservations = useMemo(() => {
    let result = reservations.filter((res) => moment(res.start).isSame(selectedDate, "day"));

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((res) => {
        return (
          (res.customerName || "").toLowerCase().includes(q) ||
          (res.phone || "").toLowerCase().includes(q) ||
          (res.tableId || "").toLowerCase().includes(q)
        );
      });
    }

    if (filters.status?.length) result = result.filter((res) => filters.status.includes(res.status));
    if (filters.source?.length) result = result.filter((res) => filters.source.includes(res.source));
    if (filters.table?.length) result = result.filter((res) => res.tableId && filters.table.includes(res.tableId));

    if (filters.location) {
      result = result.filter((res) => {
        const t = tables.find((x) => x.id === res.tableId);
        return t ? t.location === filters.location : false;
      });
    }

    return result;
  }, [reservations, selectedDate, filters, tables]);

  // -------------------------
  // Stats (safe, overnight aware)
  // -------------------------
  const overallStats = useMemo(() => {
    const totalBookings = filteredReservations.filter((r) => r.status !== "Cancelled").length;

    const openStart = moment.tz(
      `${selectedDate.format("YYYY-MM-DD")} ${ORG_HOURS.openTime}`,
      "YYYY-MM-DD HH:mm",
      ORG_HOURS.timezone
    );

    let openEnd = moment.tz(
      `${selectedDate.format("YYYY-MM-DD")} ${ORG_HOURS.closeTime}`,
      "YYYY-MM-DD HH:mm",
      ORG_HOURS.timezone
    );

    if (openEnd.isBefore(openStart)) openEnd = openEnd.add(1, "day");

    const totalOpenMinutes = openEnd.diff(openStart, "minutes");

    let totalBookedMinutes = 0;
    filteredReservations.forEach((r) => {
      if (r.status !== "Cancelled") totalBookedMinutes += moment(r.end).diff(moment(r.start), "minutes");
    });

    const averageOccupancy =
      totalOpenMinutes > 0 && filteredTables.length > 0
        ? ((totalBookedMinutes / (totalOpenMinutes * filteredTables.length)) * 100).toFixed(2)
        : "0.00";

    const totalOccupied = new Set(filteredReservations.filter((r) => r.status === "Confirmed").map((r) => r.tableId))
      .size;

    const totalAvailable = Math.max(filteredTables.length - totalOccupied, 0);

    const totalGuests = filteredReservations.reduce(
      (sum, r) => sum + (r.status !== "Cancelled" ? Number(r.partySize || 0) : 0),
      0
    );

    const utilizationRate =
      filteredTables.length > 0 ? ((totalOccupied / filteredTables.length) * 100).toFixed(2) : "0.00";

    return {
      totalBookings,
      totalOccupied,
      totalAvailable,
      totalGuests,
      averageOccupancy,
      utilizationRate,
    };
  }, [filteredReservations, selectedDate, filteredTables.length]);

  const statsList = useMemo(() => {
    const colors = ["#1677ff", "#52c41a", "#faad14", "#722ed1", "#f5222d", "#fa541c"];
    return [
      { title: "Total Bookings", value: overallStats.totalBookings, color: colors[0] },
      { title: "Occupied", value: overallStats.totalOccupied, color: colors[1] },
      { title: "Available", value: overallStats.totalAvailable, color: colors[2] },
      { title: "Guests", value: overallStats.totalGuests, color: colors[3] },
      { title: "Avg Occupancy", value: `${overallStats.averageOccupancy}%`, color: colors[4] },
      { title: "Utilization", value: `${overallStats.utilizationRate}%`, color: colors[5] },
    ];
  }, [overallStats]);

  // -------------------------
  // Header actions
  // -------------------------
  const handleDateChange = (direction) => {
    setSelectedDate((prev) => {
      if (direction === "prev") return prev.clone().subtract(1, "day");
      if (direction === "next") return prev.clone().add(1, "day");
      return moment().tz(ORG_HOURS.timezone);
    });
  };

  const handleSearch = (value) => setFilters((prev) => ({ ...prev, search: value }));
  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  // -------------------------
  // CRUD actions
  // -------------------------
  const handleAction = async (action, record) => {
    if (!record) return;

    if (action === "edit") {
      setSelectedReservation(record);
      setIsModalVisible(true);
      return;
    }

    if (action === "cancel") {
      try {
        await axios.put(
          `${apiUrl}/reservations/edit/${record.id}`,
          { status: "cancelled" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notify("success", "Reservation cancelled successfully");
        fetchReservations();
      } catch (error) {
        notify("error", error.response?.data?.error || "Failed to cancel reservation");
      }
      return;
    }

    if (action === "delete") {
      const result = await Swal.fire({
        title: "Delete reservation?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1677ff",
        cancelButtonColor: "#d33",
        confirmButtonText: "Delete",
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete(`${apiUrl}/reservations/delete/${record.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        notify("success", "Reservation deleted successfully");
        fetchReservations();
      } catch (error) {
        notify("error", error.response?.data?.error || "Failed to delete reservation");
      }
    }
  };

  // -------------------------
  // Modal submit: add/edit
  // -------------------------
  const handleAddOrEditReservation = async (data) => {
    const { reservation } = data || {};
    if (!reservation) return;

    const apiData = {
      guest_name: reservation.customerName,
      contact_number: reservation.phone,
      email: reservation.email,
      reservation_date: moment(reservation.start).format("YYYY-MM-DD"),
      reservation_time: moment(reservation.start).format("HH:mm"),
      party_size: reservation.partySize,
      assigned_table_id: reservation.tableId !== "unassigned" ? reservation.tableId : null,
      status: String(reservation.status || "confirmed").toLowerCase(),
      special_requests: reservation.notes,
      reservation_source: reservation.source,
      expected_duration_minutes: moment(reservation.end).diff(moment(reservation.start), "minutes"),
      occasion: reservation.occasion || null,
    };

    try {
      if (reservation.id) {
        await axios.put(`${apiUrl}/reservations/edit/${reservation.id}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        notify("success", "Reservation updated successfully");
      } else {
        await axios.post(`${apiUrl}/reservations/add`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        notify("success", "Reservation created successfully");
      }

      fetchReservations();
      setIsModalVisible(false);
      setSelectedReservation(null);
    } catch (error) {
      notify("error", error.response?.data?.error || "Failed to save reservation");
    }
  };

  // -------------------------
  // List view columns (click customer => drawer)
  // -------------------------
  const columns = useMemo(() => {
    return [
      {
        title: "Time",
        dataIndex: "start",
        key: "time",
        render: (text) => niceTime(text),
        sorter: (a, b) => moment(a.start).unix() - moment(b.start).unix(),
        width: 90,
      },
      {
        title: "Customer",
        dataIndex: "customerName",
        key: "customerName",
        ellipsis: true,
        render: (_, record) => (
          <button
            type="button"
            onClick={() => openDetails(record)}
            className="ns-res-customer-link"
          >
            {record.customerName || "—"}
          </button>
        ),
      },
      {
        title: "Party",
        dataIndex: "partySize",
        key: "partySize",
        width: 80,
      },
      {
        title: "Table",
        dataIndex: "tableId",
        key: "tableId",
        width: 140,
        render: (id) => (id ? tables.find((t) => t.id === id)?.label : "Unassigned") || "Unassigned",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (status) => {
          const theme = STATUS_THEME[status] || {};
          return (
            <Tag
              color={theme.bg}
              style={{
                color: theme.text,
                borderColor: theme.border,
                textDecoration: theme.strike ? "line-through" : "none",
                borderStyle: theme.dashed ? "dashed" : "solid",
                borderRadius: 999,
                fontWeight: 800,
                padding: "2px 10px",
              }}
            >
              {status}
            </Tag>
          );
        },
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 120,
      },
      {
        title: "",
        key: "actions",
        width: 64,
        render: (_, record) => (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="edit" onClick={() => handleAction("edit", record)}>
                  <span className="d-flex align-items-center gap-2">
                    <Edit3 size={16} /> Edit
                  </span>
                </Menu.Item>
                <Menu.Item key="cancel" onClick={() => handleAction("cancel", record)}>
                  <span className="d-flex align-items-center gap-2">
                    <XCircle size={16} /> Cancel
                  </span>
                </Menu.Item>
                <Menu.Item key="delete" onClick={() => handleAction("delete", record)}>
                  <span className="d-flex align-items-center gap-2">
                    <Trash2 size={16} /> Delete
                  </span>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreVertical size={18} />} />
          </Dropdown>
        ),
      },
    ];
  }, [tables, handleAction]);

  // -------------------------
  // Filters popover content
  // -------------------------
  const filterContent = useMemo(() => {
    return (
      <div style={{ width: 260 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Filters</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Floor / Location</div>
          <Select
            options={locations.map((loc) => ({ value: loc, label: loc }))}
            placeholder="All locations"
            allowClear
            style={{ width: "100%" }}
            value={filters.location}
            onChange={(value) => handleFilterChange("location", value)}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Status</div>
          <Select
            mode="multiple"
            placeholder="Select status"
            allowClear
            style={{ width: "100%" }}
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value)}
            options={[
              { value: "Pending", label: "Pending" },
              { value: "Confirmed", label: "Confirmed" },
              { value: "Cancelled", label: "Cancelled" },
              { value: "NoShow", label: "No Show" },
              { value: "CheckedIn", label: "Checked In" },
            ]}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Source</div>
          <Select
            mode="multiple"
            placeholder="Select source"
            allowClear
            style={{ width: "100%" }}
            value={filters.source}
            onChange={(value) => handleFilterChange("source", value)}
            options={[
              { value: "WalkIn", label: "Walk-In" },
              { value: "Website", label: "Website" },
              { value: "Phone", label: "Phone" },
              { value: "Partner", label: "Partner" },
            ]}
          />
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Table</div>
          <Select
            mode="multiple"
            placeholder="Select table"
            allowClear
            style={{ width: "100%" }}
            value={filters.table}
            onChange={(value) => handleFilterChange("table", value)}
            options={filteredTables.map((t) => ({ value: t.id, label: t.label }))}
          />
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <Button
            onClick={() =>
              setFilters({
                status: [],
                source: [],
                table: [],
                location: null,
                search: "",
              })
            }
            style={{ width: "100%" }}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  }, [filters, locations, filteredTables]);

  // -------------------------
  // Export helpers
  // -------------------------
  const headers = useMemo(
    () => [
      { key: "customerName", title: "Customer" },
      { key: "partySize", title: "Party Size" },
      { key: "start", title: "Start Time" },
      { key: "end", title: "End Time" },
      { key: "tableId", title: "Table" },
      { key: "status", title: "Status" },
      { key: "source", title: "Source" },
      { key: "notes", title: "Notes" },
    ],
    []
  );

  const exportData = useCallback(() => {
    return filteredReservations.map((res) => ({
      customerName: res.customerName,
      partySize: res.partySize,
      start: moment(res.start).format("HH:mm"),
      end: moment(res.end).format("HH:mm"),
      tableId: res.tableId ? tables.find((t) => t.id === res.tableId)?.label : "Unassigned",
      status: res.status,
      source: res.source,
      notes: res.notes,
    }));
  }, [filteredReservations, tables]);


  const queuedEntry = detailsReservation?.id ? waitlistIndex.get(String(detailsReservation.id)) : null;

  const footerData = useMemo(
    () => [
      { key: "Total Reservations", value: filteredReservations.length },
      { key: "Total Guests", value: filteredReservations.reduce((sum, res) => sum + Number(res.partySize || 0), 0) },
    ],
    [filteredReservations]
  );

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData(),
      headers,
      `reservations_${moment(selectedDate).format("YYYYMMDD")}.xlsx`,
      "Reservations",
      footerData
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData(),
      headers,
      `reservations_${moment(selectedDate).format("YYYYMMDD")}.csv`,
      footerData
    );
  };

  const handleExportPDF = () => setPreviewVisible(true);
  const handlePrint = () => setPreviewVisible(true);

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  // -------------------------
  // Empty state
  // -------------------------
  const EmptyState = ({ title = "No reservations found." }) => (
    <div className="ns-res-empty-state">
      <div className="ns-res-empty-icon">
        <CalendarIcon size={28} />
      </div>
      <h3>{title}</h3>
      <p>Try changing filters, selecting another date, or add a new reservation.</p>
      <Button
        type="primary"
        onClick={() => {
          setSelectedReservation(null);
          setIsModalVisible(true);
        }}
        icon={<PlusIcon size={16} />}
        className="ns-res-primary-btn"
      >
        Add Reservation
      </Button>
    </div>
  );

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="page-wrapper posmt-4 ns-res-page-wrapper">
      <div className="content container-fluid ns-res-page">
        <section className="ns-res-hero">
          <div className="ns-res-hero-left">
            <span className="ns-res-hero-icon">
              <CalendarIcon size={24} />
            </span>

            <div>
              <div className="ns-res-eyebrow">Reservations Control</div>
              <h2>Table Reservations</h2>
              <p>
                Manage bookings, table allocation, waitlist conversion, calendar visibility,
                and guest arrival flow from one premium reservation workspace.
              </p>
            </div>
          </div>

          <div className="ns-res-hero-actions">
            <Tooltip title="Reservation widget settings">
              <Link to="/pos/reservation-widget">
                <Button className="ns-res-soft-btn" icon={<SettingsIcon size={16} />}>
                  Settings
                </Button>
              </Link>
            </Tooltip>

            <Tooltip title="Refresh reservations">
              <Button className="ns-res-soft-btn" icon={<RefreshIcon size={16} />} onClick={fetchReservations}>
                Refresh
              </Button>
            </Tooltip>

            <Button
              type="primary"
              icon={<PlusIcon size={16} />}
              onClick={() => {
                setSelectedReservation(null);
                setIsModalVisible(true);
              }}
              className="ns-res-primary-btn"
            >
              New Reservation
            </Button>
          </div>
        </section>

        <section className="ns-res-control-panel">
          <div className="ns-res-date-pill">
            <Tooltip title="Previous day">
              <Button
                shape="circle"
                onClick={() => handleDateChange("prev")}
                icon={<ChevronLeft size={16} />}
              />
            </Tooltip>

            <div className="ns-res-date-copy">
              <span>Selected Date</span>
              <strong>{selectedDate.format("DD MMM YYYY")}</strong>
              <small>{selectedDate.format("dddd")}</small>
            </div>

            <Tooltip title="Next day">
              <Button
                shape="circle"
                onClick={() => handleDateChange("next")}
                icon={<ChevronRight size={16} />}
              />
            </Tooltip>

            <Button className="ns-res-today-btn" onClick={() => handleDateChange("today")}>
              Today
            </Button>
          </div>

          <div className="ns-res-search-wrap">
            <Search
              placeholder="Search customer, phone, or table..."
              allowClear
              enterButton={<SearchIcon size={16} />}
              size="large"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div className="ns-res-control-actions">
            <Popover
              content={filterContent}
              trigger="click"
              placement="bottomRight"
              overlayClassName="ns-res-filter-popover"
            >
              <Button className="ns-res-soft-btn" icon={<FilterIcon size={16} />}>
                Filters
              </Button>
            </Popover>

            <div className="ns-res-view-toggle">
              <Tooltip title="List view">
                <Button
                  type={view === "list" ? "primary" : "default"}
                  icon={<ListIcon size={16} />}
                  onClick={() => setView("list")}
                >
                  List
                </Button>
              </Tooltip>

              <Tooltip title="Calendar view">
                <Button
                  type={view === "calendar" ? "primary" : "default"}
                  icon={<CalendarIcon size={16} />}
                  onClick={() => setView("calendar")}
                >
                  Calendar
                </Button>
              </Tooltip>
            </div>

            <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
              <Button className="ns-res-soft-btn" icon={<DownloadIcon size={16} />}>
                Export
              </Button>
            </Dropdown>
          </div>
        </section>

        {/* Print/PDF Preview */}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle="Reservations"
          propertyName="Your Property Name"
          data={exportData()}
          headers={headers}
          footerData={footerData}
          totalColumns={[]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="reservations/generate-pdf"
          apiUrl={process.env.REACT_APP_API_URL || "https://api.example.com"}
        />

        <section className="ns-res-stats-grid">
          {statsList.map((stat, idx) => (
            <div
              className="ns-res-stat-card"
              key={idx}
              style={{ "--stat-tone": stat.color }}
            >
              <span>{stat.title}</span>
              <strong>{stat.value}</strong>
            </div>
          ))}
        </section>

        <section className="ns-res-content-card">
          <div className="ns-res-content-head">
            <div>
              <div className="ns-res-eyebrow">{view === "list" ? "Reservation List" : "Calendar Grid"}</div>
              <h3>{view === "list" ? "Bookings for selected date" : "Table-wise booking timeline"}</h3>
              <p>
                {filteredReservations.length} reservation{filteredReservations.length === 1 ? "" : "s"} ·{" "}
                {filteredTables.length} table{filteredTables.length === 1 ? "" : "s"} visible ·{" "}
                {overallStats.totalGuests} guest{overallStats.totalGuests === 1 ? "" : "s"}
              </p>
            </div>

            <div className="ns-res-content-badges">
              <span>{ORG_HOURS.openTime} - {ORG_HOURS.closeTime}</span>
              <span>{ORG_HOURS.timezone}</span>
            </div>
          </div>

          <div className="ns-res-content-body">
            {view === "list" ? (
              <Table
                dataSource={filteredReservations}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 15,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 15, 25, 50],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                locale={{ emptyText: <EmptyState title="No reservations for this date." /> }}
                className="ns-res-table"
                scroll={{ x: 920 }}
              />
            ) : filteredReservations.length > 0 ? (
              <div className="ns-res-calendar-shell">
                <ReservationCalendarGrid
                  dateMoment={selectedDate}
                  orgHours={ORG_HOURS}
                  tables={filteredTables}
                  reservations={filteredReservations}
                  statusTheme={STATUS_THEME}
                  showUnassignedRow
                  onCreate={(prefill) => {
                    setSelectedReservation(prefill);
                    setIsModalVisible(true);
                  }}
                  onSelect={(res) => openDetails(res)}
                />
              </div>
            ) : (
              <EmptyState title="No reservations for this date." />
            )}
          </div>
        </section>

        {/* Drawer: Reservation Details */}
        <ReservationDetailsDrawer
          open={detailsOpen}
          queuedEntry={queuedEntry}
          reservation={detailsReservation}
          tables={tables}
          timezone={ORG_HOURS.timezone}
          statusTheme={STATUS_THEME}
          onClose={closeDetails}
          onEdit={(res) => {
            closeDetails();
            setSelectedReservation(res);
            setIsModalVisible(true);
          }}
          onCancel={(res) => {
            closeDetails();
            handleAction("cancel", res);
          }}
          onDelete={(res) => {
            closeDetails();
            handleAction("delete", res);
          }}
          onAddToQueue={addReservationToQueue}
          queueLoading={queueLoading}
        />

        {/* Reservation Modal */}
        {isModalVisible && (
          <ReservationFormModal
            visible={isModalVisible}
            initial={selectedReservation}
            tables={tables}
            orgHours={ORG_HOURS}
            reservations={filteredReservations}
            onSubmit={handleAddOrEditReservation}
            onClose={() => {
              setIsModalVisible(false);
              setSelectedReservation(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
